import React from "react";
import { FileButton, ProgressBar } from "@countysuite/ui";
import { Http, HttpOptions } from "../../utils/Http";
import { readFileAsync } from "../../utils/readFileAsync";
import "./TsFileUploader.scss";

type Props = {
  onFileSelected?: (fileName: string) => void;
  onUploadComplete: (finishedFile: string) => void;
  onUploadProgress?: (pct: number) => void;
  onUploadError?: (msg: string) => void;
  uploadUrl: string;
  accept?: string;
  hideProgressBar?: boolean;
  disableButton?: boolean;
  disableUpload?: boolean;
};

type State = {
  file?: File;
  reader: FileReader;
  message: string;
  percentage: number;
  uploading: boolean;
  error: boolean;
};

type FileChunkUploadResponse = {
  serverFile: string;
};

export class TsFileUploader extends React.Component<Props, State> {
  state: State = {
    reader: new FileReader(),
    message: "",
    percentage: 0,
    uploading: false,
    error: false,
  };

  chooseFile = (files: FileList | null) => {
    if (this.state.uploading) {
      alert("An upload is already in progress.");
      return;
    }

    if (files && files.length) {
      const file = files[0];
      this.setState({
        file,
        percentage: 0,
        message: "",
        uploading: false,
        error: false,
      });

      if (this.props.onFileSelected) this.props.onFileSelected(file.name);
    }
  };

  startUpload = () => {
    this.setState({
      percentage: 0,
      message: "Uploading",
      uploading: true,
      error: false,
    });
    this.uploadNextChunk(0);
  };

  uploadNextChunk = async (start: number) => {
    const { reader, file } = this.state;
    if (!file) throw new Error("Unable to locate file!");

    //1. determine the next set of bytes we need
    const sliceSize = 256 * 1024;
    const nextSlice = start + sliceSize + 1;
    const blob = file.slice(start, nextSlice);

    try {
      //2. grab the next base64 content Chunk
      const base64Chunk = await readFileAsync(blob, reader);

      const postData = {
        begin: start <= 0,
        fileSize: file.size,
        fileData: base64Chunk.split(",")[1],
        fileName: file.name,
        fileType: file.type,
      };

      //3. upload it and handle post errors
      const options: HttpOptions = {
        url: this.props.uploadUrl,
        data: postData,
      };
      const chunkResponse = await Http.post<FileChunkUploadResponse>(options);

      if (chunkResponse.success === false) {
        throw new Error(
          chunkResponse.message || "An upload error has occurred."
        );
      }

      //4. update percentage or finish
      const sizeDone = start + sliceSize;
      const currentPercentage = Math.floor((sizeDone / file.size) * 100);

      if (nextSlice >= file.size) {
        this.setState({
          percentage: 100.0,
          uploading: false,
          error: false,
          message: "Complete",
          file: undefined,
        });
        this.props.onUploadComplete(chunkResponse.serverFile);
      } else {
        this.setState({
          percentage: currentPercentage,
          uploading: true,
          error: false,
          message: "Uploading",
        });

        if (this.props.onUploadProgress)
          this.props.onUploadProgress(currentPercentage);

        this.uploadNextChunk(nextSlice);
      }
    } catch (err) {
      this.blowChunks(err.message);
    }
  };

  blowChunks = (err: string) => {
    this.setState({
      message: err,
      error: true,
      file: undefined,
      uploading: false,
    });

    if (this.props.onUploadError) this.props.onUploadError(err);
  };

  uploadDisabled = (): boolean => {
    return this.props.disableButton || this.state.uploading === true;
  };

  componentDidUpdate = () => {
    if (!this.props.disableUpload && !this.state.uploading && this.state.file) {
      this.startUpload();
    }
  };

  render() {
    let btnText = "Choose File";
    if (this.state.uploading) {
      btnText = this.state.message + " " + this.state.percentage + "%";
    }

    const fileButton = (
      <FileButton
        name="importFile"
        onChange={(e) => this.chooseFile(e.target.files)}
        accept={this.props.accept}
        disabled={this.props.disableButton || this.state.uploading}
      >
        {btnText}
      </FileButton>
    );
    if (this.props.hideProgressBar) return fileButton;

    return (
      <div className="file-uploader-controls">
        {this.props.children}
        <div className="flex-bar">
          <div className="bar-btn-wrapper">{fileButton}</div>
          <div className="bar-notification">
            <ProgressBar
              percent={this.state.percentage}
              message={this.state.message}
              error={this.state.error}
            />
          </div>
        </div>
      </div>
    );
  }
}
